import {
    type ScanReviewScene,
    useScanReviewAppContext,
    ScanReviewViewType,
    ScanReviewDisplayType,
    useScanReviewMarginMarkingViewContext,
    type QcHeatmapRange,
    useScanReviewCompleteViewAppContext,
    QCColorLegend,
} from '@orthly/dentin';
import { HeatMapType } from '@orthly/forceps';
import { type ToothNumber, AllToothNumbers, AllLowerToothNumbers } from '@orthly/items';
import { StackY, styled, FlossPalette, NumberArrayFlipperInput, StackX } from '@orthly/ui';
import { Button, Box, FormControlLabel, Checkbox } from '@orthly/ui-primitives';
import React from 'react';

const ToolPanel = styled(Box)({
    borderRadius: 8,
    margin: '4px',
    border: `1px solid ${FlossPalette.STROKE_LIGHT}`,
    backgroundColor: FlossPalette.WHITE,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
});

const ToothNumberPanel = styled(Box)({
    borderRadius: 4,
    margin: '4px',
    border: `1px solid ${FlossPalette.STROKE_LIGHT}`,
    backgroundColor: FlossPalette.WHITE,
    overflow: 'scroll',
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    flexDirection: 'column',
    maxHeight: '150px',
    minHeight: 0,
    minWidth: 0,
});

const QCToolbarContainer = styled(StackY)({
    position: 'absolute',
    zIndex: 21,
    top: 86,
    right: 16,
});

export const QCToolbar: React.VFC = () => {
    const ctx = useScanReviewCompleteViewAppContext();
    return (
        <QCToolbarContainer>
            <QCColorLegend
                heatMapType={HeatMapType.Clearance}
                heatMapRange={ctx.heatMapRange}
                setHeatMapRange={qc => ctx.setHeatMapRangeCallback({ min: qc.min, max: qc.max })}
                dynamicHeatmaps={true}
                heatMapStep={0.05}
            />
        </QCToolbarContainer>
    );
};

export const ScanReviewCompleteViewToolButtonsToolbar: React.VFC<{
    marginMarking: boolean;
    setMarginMarking: React.Dispatch<React.SetStateAction<boolean>>;
    scene: ScanReviewScene;
    heatMapRange: QcHeatmapRange | null;
}> = ({ marginMarking, setMarginMarking, scene, heatMapRange }) => {
    const { displayType, setDisplayTypeCallback, viewType } = useScanReviewAppContext();
    return (
        <StackX>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={displayType === ScanReviewDisplayType.Scan}
                        onChange={e =>
                            e.target.checked && setDisplayTypeCallback(scene, ScanReviewDisplayType.Scan, heatMapRange)
                        }
                    />
                }
                label={'Scan'}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={displayType === ScanReviewDisplayType.StoneModel}
                        onChange={e =>
                            e.target.checked &&
                            setDisplayTypeCallback(scene, ScanReviewDisplayType.StoneModel, heatMapRange)
                        }
                    />
                }
                label={'Stone Model'}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={displayType === ScanReviewDisplayType.BiteAnalysis}
                        onChange={e => {
                            if (e.target.checked) {
                                setMarginMarking(false);
                                setDisplayTypeCallback(scene, ScanReviewDisplayType.BiteAnalysis, heatMapRange);
                            }
                        }}
                    />
                }
                label={'Bite Analysis'}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        disabled={viewType !== ScanReviewViewType.Single}
                        checked={marginMarking}
                        onChange={e => {
                            if (e.target.checked && displayType === ScanReviewDisplayType.BiteAnalysis) {
                                setDisplayTypeCallback(scene, ScanReviewDisplayType.Scan, heatMapRange);
                            }
                            setMarginMarking(e.target.checked);
                        }}
                    />
                }
                label={'Margin Marking'}
            />
        </StackX>
    );
};

export const ScanReviewMarginMarkingViewToolbar: React.VFC<{
    preppedTeeth: Map<ToothNumber, boolean>;
    setPreppedTeeth: React.Dispatch<React.SetStateAction<Map<ToothNumber, boolean>>>;
}> = ({ preppedTeeth, setPreppedTeeth }) => {
    const { scene, activeTooth, setActiveTooth, toolController } = useScanReviewMarginMarkingViewContext();
    const { setJawDisplayStateCallback } = useScanReviewAppContext();
    return (
        <StackY>
            <ToolPanel>
                <Button
                    variant={'text'}
                    onClick={() => {
                        toolController?.reset();
                    }}
                >
                    Reset
                </Button>
                <Button
                    variant={'text'}
                    onClick={() => {
                        toolController?.undo();
                    }}
                >
                    Undo
                </Button>
            </ToolPanel>
            <ToothNumberPanel>
                {AllToothNumbers.map(unn => (
                    <FormControlLabel
                        key={`prepped-tooth-${unn}`}
                        control={
                            <Checkbox
                                checked={preppedTeeth.get(unn)}
                                onChange={e => {
                                    const updatedPreppedTeeth: Map<ToothNumber, boolean> = new Map([
                                        ...preppedTeeth,
                                    ]).set(unn, e.target.checked);
                                    const checkedTeeth: ToothNumber[] = [...updatedPreppedTeeth]
                                        .filter(([_key, value]) => value)
                                        .map(([key, _value]) => key);
                                    if (activeTooth === undefined || !updatedPreppedTeeth.get(activeTooth)) {
                                        let newActiveTooth = undefined;
                                        if (checkedTeeth) {
                                            newActiveTooth = checkedTeeth.length
                                                ? (Math.min(...checkedTeeth) as ToothNumber)
                                                : undefined;
                                        }
                                        setActiveTooth(newActiveTooth);
                                    } else if (!checkedTeeth) {
                                        setActiveTooth(undefined);
                                    }
                                    setPreppedTeeth(updatedPreppedTeeth);
                                }}
                            />
                        }
                        label={unn}
                    />
                ))}
            </ToothNumberPanel>
            {activeTooth !== undefined && (
                <NumberArrayFlipperInput
                    value={activeTooth}
                    onChange={unn => {
                        const isLowerTooth = AllLowerToothNumbers.filter(n => n === unn).length > 0;
                        if (isLowerTooth) {
                            setJawDisplayStateCallback(scene, {
                                lower: true,
                                upper: false,
                            });
                        } else {
                            setJawDisplayStateCallback(scene, {
                                lower: false,
                                upper: true,
                            });
                        }
                        setActiveTooth(unn as ToothNumber);
                    }}
                    allowedValues={[...preppedTeeth]
                        .filter(([_key, value]) => value === true)
                        .map(([key, _value]) => key)}
                />
            )}
        </StackY>
    );
};

export const ScanReviewCompleteViewButtonsToolbar: React.VFC<{
    setMarginMarking: React.Dispatch<React.SetStateAction<boolean>>;
    scene: ScanReviewScene;
}> = ({ setMarginMarking, scene }) => {
    const { setJawDisplayStateCallback, jawDisplayState, displayType, setViewType } = useScanReviewAppContext();
    return (
        <StackY>
            <ToolPanel>
                <Button variant={'text'} onClick={() => setViewType(ScanReviewViewType.Single)}>
                    Single View
                </Button>
                <Button
                    variant={'text'}
                    onClick={() => {
                        setViewType(ScanReviewViewType.SideBySide);
                        setMarginMarking(false);
                    }}
                >
                    Side By Side
                </Button>
                <Button
                    variant={'text'}
                    onClick={() => {
                        setViewType(ScanReviewViewType.Complete);
                        setMarginMarking(false);
                    }}
                >
                    Complete View
                </Button>
            </ToolPanel>
            <ToolPanel>
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={e =>
                                setJawDisplayStateCallback(scene, { ...jawDisplayState, upper: e.target.checked })
                            }
                            checked={jawDisplayState.upper}
                        />
                    }
                    label={'Upper'}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            onChange={e =>
                                setJawDisplayStateCallback(scene, { ...jawDisplayState, lower: e.target.checked })
                            }
                            checked={jawDisplayState.lower}
                        />
                    }
                    label={'Lower'}
                />
            </ToolPanel>

            {displayType === ScanReviewDisplayType.BiteAnalysis && <QCToolbar />}
        </StackY>
    );
};
